/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from 'react';
import { useTranslation } from 'react-i18next';

interface ProductInfo {
  id: string;
  name: string;
  manufacturer: string;
  sku: string;
}

interface Errors {
  selectedProductId?: string;
  lotId?: string;
  manufactureDate?: string;
  expiryDate?: string;
  receivedGms?: string;
  numContainers?: string;
}

interface LogNewShipmentFormProps {
  formData: any;
  productData: { manufacturer: string; sku: string };
  products: ProductInfo[];
  loadingProducts: boolean;
  errors: Errors;
  translationKey: string;

  onChange: (field: string, value: any) => void;
  onSave: () => void;
  isSaving: boolean;
  onCancel: () => void;
}

const LogNewShipmentForm: React.FC<LogNewShipmentFormProps> = ({
  formData,
  productData,
  products,
  loadingProducts,
  errors,
  translationKey,
  onChange,
  onSave,
  isSaving,
  onCancel,
}) => {
  const { t } = useTranslation();

  return (
    <div className="media-prep-scope p-4 max-w-full mx-auto animate-in fade-in slide-in-from-bottom duration-500 pb-24">
      {/* Product Selection Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-3">
        <div className="px-6 py-2 bg-white border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-[#FF7043] text-xl relative -top-[4px]">{t("mediaPowder.icons.search")}</span>
            <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t(`${translationKey}.form.productSelection`)}</h3>
          </div>
          <span className="bg-[#2D3E50] text-white text-[9px] font-black px-2 py-1 rounded uppercase tracking-wider">{t(`${translationKey}.form.GMP`)}</span>
        </div>
        <div className="pt-2 px-8 pb-8 space-y-4">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.mediaProduct`)} <span className="text-red-500">*</span></label>
            <div className="relative">
              <select 
                value={formData.selectedProductId}
                onChange={(e) => onChange("selectedProductId", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] pl-4 pr-10 text-sm font-medium text-slate-700 appearance-none bg-white"
              >
                <option value="">{loadingProducts ? "Loading products..." : t(`${translationKey}.form.placeholders.productSearch`)}</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {errors.selectedProductId && (<p className="errorText">{errors.selectedProductId}</p>)}              
              <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">{t("mediaPowder.icons.expand_more")}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.manufacturer`)}</label>
              <input 
                type="text" 
                value={productData.manufacturer}
                readOnly
                placeholder={t(`${translationKey}.form.placeholders.manufacturer`)}
                className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm text-slate-500 cursor-not-allowed shadow-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block text-left">{t(`${translationKey}.form.sku`)}</label>
              <input 
                type="text" 
                value={productData.sku}
                readOnly
                placeholder={t(`${translationKey}.form.placeholders.sku`)}
                className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm text-slate-500 cursor-not-allowed shadow-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Lot Details Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-2 bg-white border-b border-slate-100 flex items-center gap-1">
          <span className="material-symbols-outlined text-[#FF7043] text-xl relative -top-[4px]">{t("mediaPowder.icons.inventory2")}</span>
          <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t(`${translationKey}.form.lotDetails`)}</h3>
        </div>
        <div className="pt-2 px-8 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.lotId`)} <span className="text-red-500">*</span></label>
              <input 
                type="text" 
                value={formData.lotId}
                onChange={(e) => onChange("lotId", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-800"
                placeholder={t(`${translationKey}.form.placeholders.lotId`)}
              />
              {errors.lotId && <p className="errorText">{errors.lotId}</p>}
            </div>
            {/* <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.grn`)}</label>
              <input 
                type="text" 
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700"
                placeholder={t(`${translationKey}.form.placeholders.grn`)}
              />
            </div> */}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.manufactureDate`)} <span className="text-red-500">*</span></label>
              <input 
                type="date" 
                value={formData.manufactureDate}
                onChange={(e) => onChange("manufactureDate", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700"
              />
              {errors.manufactureDate && (<p className="errorText">{errors.manufactureDate}</p>)}
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.expiryDate`)} <span className="text-red-500">*</span></label>
              <input 
                type="date" 
                value={formData.expiryDate}
                onChange={(e) => onChange("expiryDate", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700"
              />
              {errors.expiryDate && (<p className="errorText">{errors.expiryDate}</p>)}
            </div>
          </div>
        </div>
      </div>

      {/* Quantity Tracking Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-2 bg-white border-b border-slate-100 flex items-center gap-1">
          <span className="material-symbols-outlined text-[#FF7043] text-xl relative -top-[4px]">{t("mediaPowder.icons.bar_chart")}</span>
          <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">{t(`${translationKey}.form.quantityTracking`)}</h3>
        </div>
        <div className="pt-2 px-8 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.totalQuantity`)} <span className="text-red-500">*</span></label>
              <input 
                type="number" 
                value={formData.receivedGms}
                onChange={(e) => onChange("receivedGms", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700"
                placeholder={t(`${translationKey}.form.placeholders.quantity`)}
              />
              {errors.receivedGms && (<p className="errorText">{errors.receivedGms}</p>)}
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block text-left">{t(`${translationKey}.form.numContainers`)} <span className="text-red-500">*</span></label>
              <input 
                type="number" 
                value={formData.numContainers}
                onChange={(e) => onChange("numContainers", e.target.value)}
                className="w-full h-10 rounded-lg border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#FF7043] focus:border-[#FF7043] px-4 text-sm font-medium text-slate-700"
                placeholder={t(`${translationKey}.form.placeholders.containers`)}
              />
              {errors.numContainers && (<p className="errorText">{errors.numContainers}</p>)}
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block text-left">{t(`${translationKey}.form.qtyPerContainer`)}</label>
              <div className="relative">
                <input 
                  type="text" 
                  value={formData.qtyPerContainer}
                  readOnly
                  className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm text-slate-500 cursor-not-allowed shadow-none"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-300 italic">{t(`${translationKey}.form.gmsPerContainer`)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-4 bg-transparent mt-8">
        <button 
          onClick={onCancel}
          className="px-10 py-3 bg-white border border-slate-200 rounded-lg text-xs font-black text-slate-600 uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
        >
          {t("buttons.cancel")}
        </button>
        <button 
          onClick={onSave}
          disabled={isSaving}
          className="bg-[#FF7043] text-white flex items-center justify-center gap-3 px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border border-[#FF7043] shadow-none focus:outline-none"
        >
          <span className="material-symbols-outlined text-lg">{t("buttons.save")}</span>
          {t("buttons.addToInventory")}
        </button>
      </div>
    </div>
  );
};

export default LogNewShipmentForm;